import React, { useState, useEffect } from "react";
import qs from "query-string";
import { useSelector } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { object, string, number, array, bool } from "yup";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { Row, Card, Form, Space, Spinner } from "@masterteam/mt-design-react";

import api from "../../providers/api";
import { addToast, findUnitLabel } from "../../helpers";

import Steps from "../Steps";
import Header from "./Header";
import History from "./History";
import FormHeader from "../FormHeader";
import Attachments from "./Attachments";
import RequestDetails from "./RequestDetails";
import ChangeKPIDetails from "./ChangeKPIDetails";
import ConfirmationModal from "../ConfirmationModal";

import "./style.scss";

const validationSchema = (strategyType) =>
  object().shape({
    RequestDescription: string().required(" "),
    ArabicName: string().required(" "),
    EnglishName: string().required(" "),
    ArabicDescription: string().required(" "),
    EnglishDescription: string().required(" "),
    ArabicEquation: string().required(" "),
    EnglishEquation: string().required(" "),
    Source: string(),
    Code: string().required(" "),
    Order: number().required(" ").min(1),
    OwnerModel: object().required(" ").nullable(),
    ObjectiveOwnerModel: object().required(" ").nullable(),
    UnitOfMeasure: object().required(" ").nullable(),
    EnglishUnitDetails: string().when("UnitOfMeasure", {
      is: (selectedUnit) => selectedUnit.value === "Value",
      then: (schema) => schema.required(" ")
    }),
    ArabicUnitDetails: string().when("UnitOfMeasure", {
      is: (selectedUnit) => selectedUnit.value === "Value",
      then: (schema) => schema.required(" ")
    }),
    KPISectors: array().of(object()).min(1, " "),
    Polarity: object().required(" ").nullable(),
    Theme:
      +strategyType === 1
        ? object().nullable().required(" ")
        : object().nullable(),
    Pillar:
      +strategyType === 2
        ? object().nullable().required(" ")
        : object().nullable(),
    Level:
      +strategyType === 1
        ? object().nullable().required(" ")
        : object().nullable(),
    Objective: object().required(" ").nullable()
  });

const KPIChangeRequest = ({ location }) => {
  const intl = useIntl();
  const { kpi, type } = qs.parse(location.search);
  const { id } = useParams();
  const history = useHistory();
  const [kpiDetails, setKpiDetails] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [kpiDetailsLoading, setKpiDetailsLoading] = useState(false);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStrategic, setIsStrategic] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [KPIChangeType, user, objectiveLevels, polarities] = useSelector(
    ({ theme, user }) => [
      theme.ChangeRequestChangeType.item?.Items,
      user.user,
      theme?.ObjectiveLevel?.item?.Items,
      theme.Polarities.item.Items
    ]
  );

  const [lists, setLists] = useState({
    users: [],
    themes: [],
    pillars: []
  });

  const [initialValues, setInitialValues] = useState({
    ChangeType: type,
    CreatedBy: user.DisplayName,
    Created: new Date(),
    RequestDescription: "",
    EnglishName: "",
    ArabicName: "",
    EnglishDescription: "",
    ArabicDescription: "",
    EnglishEquation: "",
    ArabicEquation: "",
    Source: "",
    Code: "",
    Order: "",
    OwnerModel: null,
    ObjectiveOwnerModel: null,
    UnitOfMeasure: null,
    EnglishUnitDetails: "",
    ArabicUnitDetails: "",
    KPISectors: [],
    Theme: null,
    Pillar: null,
    Level: null,
    Objective: null,
    Polarity: polarities[0]
  });

  const getLists = async () => {
    setIsLoading(true);
    try {
      const [usersRes, themesRes, pillarsRes, sectorsRes] = await Promise.all([
        api.user.getAll(),
        api.objective.getObjectivesByThemes(),
        api.objective.getObjectivesByPillars(),
        api.orgStructure.getAllTree()
      ]);
      const newLists = {
        ...lists,
        users: usersRes.StatusCode === "Success" ? usersRes.Data : [],
        themes: themesRes.StatusCode === "Success" ? themesRes.Data : [],
        pillars: pillarsRes.StatusCode === "Success" ? pillarsRes.Data : [],
        sectors: sectorsRes.StatusCode === "Success" ? sectorsRes.Data : []
      };
      setLists(newLists);

      if (id) {
        await getRequestData(newLists);
      }

      setIsLoading(false);
      return newLists;
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getKpiDetails = async (id, lists) => {
    setKpiDetailsLoading(true);
    let kpiRes;

    try {
      kpiRes = await api.kpi.getById(id);
      const isStrategic = kpiRes?.Data?.Objective?.Theme ? true : false;

      const theme = isStrategic
        ? lists?.themes?.find(
            (e) => e.ID === kpiRes?.Data?.Objective?.Theme?.ID
          )
        : null;

      const pillar = isStrategic
        ? null
        : lists.pillars?.find(
            (e) => e.ID === kpiRes?.Data?.Objective?.Pillar?.ID
          );

      const sectors = kpiRes?.Data?.KPISectors?.length
        ? kpiRes?.Data?.KPISectors?.map((e) =>
            lists?.sectors?.find((item) => item.ID === e.ID)
          )
        : [];

      if (kpiRes?.StatusCode === "Success") {
        setIsStrategic(+kpiRes.Data?.Objective?.StrategyType === 1);
        setInitialValues({
          ...initialValues,
          EnglishName: kpiRes.Data?.EnglishName,
          ArabicName: kpiRes.Data?.ArabicName,
          EnglishDescription: kpiRes.Data?.EnglishDescription,
          ArabicDescription: kpiRes.Data?.ArabicDescription,
          EnglishEquation: kpiRes.Data?.EnglishEquation,
          ArabicEquation: kpiRes.Data?.ArabicEquation,
          Source: kpiRes.Data?.Source,
          Code: kpiRes.Data?.Code,
          Order: kpiRes.Data?.Order,
          OwnerModel: kpiRes.Data?.OwnerModel,
          ObjectiveOwnerModel: kpiRes.Data?.ObjectiveOwnerModel,
          UnitOfMeasure: findUnitLabel(kpiRes?.Data.UnitOfMeasure),
          EnglishUnitDetails: kpiRes.Data?.EnglishUnitDetails,
          ArabicUnitDetails: kpiRes.Data?.ArabicUnitDetails,
          Objective: kpiRes.Data?.Objective,
          KPISectors: sectors,
          Theme: theme,
          Pillar: pillar,
          Polarity: polarities.find(
            (item) => item.Value === kpiRes?.Data?.Polarity
          ),
          Level: objectiveLevels.find(
            (item) => +item.Value === +kpiRes?.Data.Objective?.Level
          ),
          Attachments: kpiRes?.Data?.Attachments
        });
        setKpiDetails(kpiRes.Data);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setKpiDetailsLoading(false);
    }

    if (kpiRes?.StatusCode === "Success") {
      return kpiRes.Data;
    }
  };

  const getRequestData = async (lists) => {
    setRequestIsLoading(true);
    try {
      const res = await api.workflow.getRequestByID(id);
      const Form = res?.Data?.Form;

      setRequestData(res?.Data);
      setIsReadOnly(res?.Data?.Status !== 7);

      const isStrategic = Form?.KPIDetails.Objective?.Theme ? true : false;

      const theme = isStrategic
        ? lists?.themes?.find(
            (e) => e.ID === Form?.KPIDetails?.Objective?.Theme?.ID
          )
        : null;

      const pillar = isStrategic
        ? null
        : lists.pillars?.find(
            (e) => e.ID === Form?.KPIDetails?.Objective?.Pillar?.ID
          );

      const sectors = Form?.KPIDetails?.KPISectors?.length
        ? Form?.KPIDetails?.KPISectors?.map((e) =>
            lists?.sectors?.find((item) => item.ID === e.ID)
          )
        : [];

      setIsStrategic(+Form?.KPIDetails?.Objective?.StrategyType === 1);
      setKpiDetails(Form?.OldKPIDetails);
      setInitialValues({
        ...initialValues,
        RequestDescription: Form?.RequestDescription,
        ChangeType: type ?? Form?.ChangeType,
        EnglishName: Form?.KPIDetails?.EnglishName,
        ArabicName: Form?.KPIDetails?.ArabicName,
        EnglishDescription: Form?.KPIDetails?.EnglishDescription,
        ArabicDescription: Form?.KPIDetails?.ArabicDescription,
        EnglishEquation: Form?.KPIDetails?.EnglishEquation,
        ArabicEquation: Form?.KPIDetails?.ArabicEquation,
        Source: Form?.KPIDetails?.Source,
        Code: Form?.KPIDetails?.Code,
        Order: Form?.KPIDetails?.Order,
        OwnerModel: Form?.KPIDetails?.OwnerModel,
        ObjectiveOwnerModel: Form?.KPIDetails?.ObjectiveOwnerModel,
        UnitOfMeasure: findUnitLabel(Form?.KPIDetails?.UnitOfMeasure),
        EnglishUnitDetails: Form?.KPIDetails?.EnglishUnitDetails,
        ArabicUnitDetails: Form?.KPIDetails?.ArabicUnitDetails,
        Objective: Form?.KPIDetails?.Objective,
        Polarity: polarities.find(
          (item) => item.Value === Form?.KPIDetails.Polarity
        ),
        KPISectors: sectors,
        Theme: theme,
        Pillar: pillar,
        Level: objectiveLevels.find(
          (item) => +item.Value === +Form?.KPIDetails?.Objective.Level
        ),
        Attachments: res?.Data?.Attachments,
        CreatedBy: res?.Data?.RequestedBy?.DisplayName
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData"
      });
    } finally {
      setRequestIsLoading(false);
    }
  };

  const onSubmit = async (values, isDraft = false) => {
    setIsSubmitting(true);
    try {
      let attachments = [];

      if (values?.Attachments?.length) {
        const attachmentsRes = await api.attachment.create(values?.Attachments);
        attachments = attachmentsRes?.Data?.map((item) => ({ ID: item }));
      }

      const body = {
        RequestID: id || 0,
        KPIID: kpiDetails?.ID,
        RequestDescription: values?.RequestDescription,
        ChangeType: values?.ChangeType,
        IsDraft: isDraft,
        KPIDetails: {
          ...values,
          UnitOfMeasure: values?.UnitOfMeasure?.value,
          Polarity: values.Polarity?.Value
        },
        OldKPIDetails: { ...kpiDetails },
        Attachments: attachments
      };

      const res = await api.kpi.changeKPIRequest(body);

      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.sendOperation");
        history.push(`/splus/kpi-details/${kpiDetails?.ID}`);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function fetchData() {
    const currentLists = await getLists();
    if (kpi) getKpiDetails(kpi, currentLists);
  }

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoading || kpiDetailsLoading || requestIsLoading)
    return (
      <Row justify="center" style={{ marginTop: "60px" }}>
        <Spinner size="large" />
      </Row>
    );

  return (
    <>
      <Header kpiDetails={kpiDetails} />
      <Card className="kpi-change-request">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema(
            kpiDetails?.Objective?.StrategyType
          )}
          onSubmit={() => {
            setIsConfirmationModalOpen(true);
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <>
              <FormHeader
                canApprove={requestData?.CanApprove}
                isReadOnly={isReadOnly}
                getData={() => {
                  getRequestData(lists);
                }}
                data={requestData}
                title="change-request-form"
                cancelRoute={`/splus/kpi-details/${kpiDetails?.ID}`}
                handleSaveAsDraft={() => {
                  onSubmit(values, true);
                }}
              />

              {isReadOnly ? <Steps steps={requestData?.Steps} /> : null}

              <Space className="form-body" direction="vertical" size="large">
                <Space className="request-type-wrapper">
                  <div className="label">
                    <FormattedMessage id="change-type" /> :
                  </div>
                  <div className="request-type">
                    <FormattedMessage id="change-kpi-details" />
                  </div>
                </Space>

                <RequestDetails
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                />
                <ChangeKPIDetails
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                  lists={lists}
                  values={values}
                  setFieldValue={setFieldValue}
                  isStrategic={isStrategic}
                />
                <Attachments
                  values={values}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  setFieldValue={setFieldValue}
                />

                {isReadOnly ? <History data={requestData?.WFHistory} /> : null}
              </Space>

              {isConfirmationModalOpen && (
                <ConfirmationModal
                  isOpen={isConfirmationModalOpen}
                  title={intl.formatMessage({ id: "kpi-change-request" })}
                  closeModal={() => {
                    setIsConfirmationModalOpen(false);
                    addToast.error("toast.cancelOperation");
                  }}
                  handleSubmit={() => {
                    onSubmit(values, false);
                  }}
                  ButtonStatus={isSubmitting}
                />
              )}
            </>
          )}
        </Form>
      </Card>
    </>
  );
};

export default withRouter(KPIChangeRequest);
